import React from "react"
import { Stack } from "@mui/material"
import { ViraTextField } from "../atoms/ViraTextField"
import { ViraText } from "../atoms/ViraText"
import { SegmentedToggle } from "./SegmentedToggle"
import { typeScale, typography } from "../theme/tokens"

export type Meridiem = "AM" | "PM"

export type TimeOfDayParts = {
  hour12: number
  minute: number
  period: Meridiem
}

/** Parse stored 24h `HH:mm` (or `H:mm`) into 12h parts. */
export function hhmmToParts(value: string | undefined | null): TimeOfDayParts {
  const match = (value ?? "").trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!match) {
    return { hour12: 10, minute: 0, period: "AM" }
  }
  let hour24 = Number(match[1])
  const minute = Number(match[2])
  if (!Number.isFinite(hour24) || hour24 < 0 || hour24 > 23) {
    hour24 = 10
  }
  const safeMinute =
    Number.isFinite(minute) && minute >= 0 && minute <= 59 ? minute : 0
  const period: Meridiem = hour24 >= 12 ? "PM" : "AM"
  let hour12 = hour24 % 12
  if (hour12 === 0) {
    hour12 = 12
  }
  return { hour12, minute: safeMinute, period }
}

/** Convert 12h parts back to zero-padded 24h `HH:mm`. */
export function partsToHhmm(
  hour12: number,
  minute: number,
  period: Meridiem
): string {
  let h = Math.min(12, Math.max(1, Math.floor(hour12) || 12))
  const m = Math.min(59, Math.max(0, Math.floor(minute) || 0))
  if (period === "AM") {
    h = h === 12 ? 0 : h
  } else {
    h = h === 12 ? 12 : h + 12
  }
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

export type TimeOfDayFieldProps = {
  /** 24h `HH:mm` value used by schedule persistence. */
  value?: string
  onChange?: (hhmm: string) => void
  label?: string
  disabled?: boolean
}

/**
 * Clinician-friendly 12-hour time (hour + minute + AM/PM).
 * Value in/out remains 24h `HH:mm` for Hasura / Nest delivery.
 */
export function TimeOfDayField({
  value = "10:00",
  onChange,
  label = "Start time",
  disabled = false,
}: TimeOfDayFieldProps) {
  const parts = hhmmToParts(value)
  const [hourDraft, setHourDraft] = React.useState(String(parts.hour12))
  const [minuteDraft, setMinuteDraft] = React.useState(
    String(parts.minute).padStart(2, "0")
  )

  React.useEffect(() => {
    const next = hhmmToParts(value)
    setHourDraft(String(next.hour12))
    setMinuteDraft(String(next.minute).padStart(2, "0"))
  }, [value])

  const emit = (hour12: number, minute: number, period: Meridiem) => {
    onChange?.(partsToHhmm(hour12, minute, period))
  }

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {label ? (
        <ViraText
          tone="muted"
          sx={{
            fontFamily: typography.fontFamily.body,
            fontSize: typeScale.meta,
            fontWeight: 500,
          }}
        >
          {label}
        </ViraText>
      ) : null}
      <Stack direction="row" spacing={1} alignItems="center">
        <ViraTextField
          size="small"
          label="Hour"
          type="number"
          disabled={disabled}
          value={hourDraft}
          onChange={(e) => {
            const raw = e.target.value
            setHourDraft(raw)
            if (raw === "") {
              return
            }
            const hour12 = Number.parseInt(raw, 10)
            if (!Number.isFinite(hour12)) {
              return
            }
            const clamped = Math.min(12, Math.max(1, hour12))
            emit(clamped, parts.minute, parts.period)
          }}
          onBlur={() => {
            const hour12 = Math.min(
              12,
              Math.max(1, Number.parseInt(hourDraft, 10) || parts.hour12)
            )
            setHourDraft(String(hour12))
            emit(hour12, parts.minute, parts.period)
          }}
          sx={{ width: 88 }}
          inputProps={{ min: 1, max: 12, "aria-label": "Hour" }}
        />
        <ViraText tone="ink" sx={{ fontWeight: 700, px: 0.25 }}>
          :
        </ViraText>
        <ViraTextField
          size="small"
          label="Minute"
          type="number"
          disabled={disabled}
          value={minuteDraft}
          onChange={(e) => {
            const raw = e.target.value
            setMinuteDraft(raw)
            if (raw === "") {
              return
            }
            const minute = Number.parseInt(raw, 10)
            if (!Number.isFinite(minute)) {
              return
            }
            const clamped = Math.min(59, Math.max(0, minute))
            emit(parts.hour12, clamped, parts.period)
          }}
          onBlur={() => {
            const minute = Math.min(
              59,
              Math.max(0, Number.parseInt(minuteDraft, 10) || 0)
            )
            setMinuteDraft(String(minute).padStart(2, "0"))
            emit(parts.hour12, minute, parts.period)
          }}
          sx={{ width: 96 }}
          inputProps={{ min: 0, max: 59, "aria-label": "Minute" }}
        />
        <SegmentedToggle
          fullWidth={false}
          aria-label="AM or PM"
          value={parts.period}
          onChange={(period) => emit(parts.hour12, parts.minute, period)}
          options={[
            { value: "AM", label: "AM", disabled },
            { value: "PM", label: "PM", disabled },
          ]}
        />
      </Stack>
    </Stack>
  )
}
